import { Grid, Link, ListItemButton, Stack, Typography } from '@mui/material';
import { CuriosityNamespaceType, CuriosityValue } from '@procureai/curiosity-ui-mui';
import { formatNumber, NumberFormatPreset, useCurrency } from '@procureai/ui-mui';
import { Tooltip, TooltipContent, TooltipTrigger } from '@procureai/uix';
import { secondsToMilliseconds } from 'date-fns';
import { isNotNil } from 'ramda';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Union } from 'ts-toolbelt';

import { useTrackSearchResults } from '../../hooks/useTrackSearchResults/useTrackSearchResults.ts';
import { SearchIndexDates, SearchIndexId, SearchIndexSourceRefence } from '../../types/SearchIndex.ts';
import { ClampedMultiline } from '~/components/ClampedMultiline/ClampedMultiline';
import { Target } from '~/hooks/useTrackInteraction/useTrackInteraction';
import { RouteId } from '~/routes';
import { eventStopPropagation } from '~/util/function/eventStopPropagation';

export interface SearchHitPurchaseRequisitionPropsHit
  extends SearchIndexId, SearchIndexDates, SearchIndexSourceRefence {
  title: string;
  description: string;
  unitId: string;
  currency: string;
  requestedAtTimestamp: number;
  pricePerUnitFloat: number;
  valueFloat: Union.Nullable<number>;
  quantityFloat: Union.Nullable<number>;
  supplierId: string;
  commodityGroupId: string;
  parent: SearchIndexId &
    SearchIndexDates &
    SearchIndexSourceRefence & {
      status: string;
    };
}

interface SearchHitPurchaseRequisitionProps {
  hit: SearchHitPurchaseRequisitionPropsHit;
}
export const SearchHitPurchaseRequisition = ({ hit }: SearchHitPurchaseRequisitionProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'SearchHitPurchaseRequisition' });
  const { t: tStatus } = useTranslation(undefined, { keyPrefix: 'PurchaseRequisitionStatus' });
  const { format } = useCurrency({ code: hit.currency });
  const navigate = useNavigate();

  const itemLink = RouteId.purchaseRequisitionDetail(hit.parent.id);

  const handleItemClicked = useTrackSearchResults({ hit, target: Target.SEARCH_RESULT_PURCHASE_REQUISITION });

  return (
    <ListItemButton
      onClick={(event) => {
        handleItemClicked(event);
        navigate(itemLink);
      }}
    >
      <Grid container sx={{ flexGrow: 1 }}>
        <Grid size={9}>
          <Link component={RouterLink} to={itemLink} variant="subtitle1" onClick={handleItemClicked} underline="hover">
            {hit.title}
          </Link>
          <Typography
            component="div"
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('date', {
              createdAt: secondsToMilliseconds(hit.createdAtTimestamp),
              requestedAt: secondsToMilliseconds(hit.requestedAtTimestamp),
            })}
          </Typography>
          {hit.description ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <ClampedMultiline lines={3} variant="body2" color="text.secondary">
                    {hit.description}
                  </ClampedMultiline>
                }
              />
              <TooltipContent side="right" className="max-w-96 bg-neutral-600 [&_svg]:invisible">
                {hit.description}
              </TooltipContent>
            </Tooltip>
          ) : null}
          <Stack
            direction="row"
            sx={{
              flexWrap: 'wrap',
              columnGap: 2,
              marginTop: 1,
            }}
          >
            {hit.supplierId ? (
              <Stack direction="row" spacing={1}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {t('supplier')}
                </Typography>
                <Link
                  component={RouterLink}
                  to={RouteId.supplierDetail(hit.supplierId)}
                  variant="body2"
                  onClick={eventStopPropagation}
                >
                  <CuriosityValue ns={CuriosityNamespaceType.SUPPLIER} i18nKey={hit.supplierId} />
                </Link>
              </Stack>
            ) : null}

            {hit.commodityGroupId ? (
              <Stack direction="row" spacing={1}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {t('commodityGroup')}
                </Typography>
                <Link
                  component={RouterLink}
                  to={RouteId.commodityGroupDetail(hit.commodityGroupId)}
                  variant="body2"
                  onClick={eventStopPropagation}
                >
                  <CuriosityValue ns={CuriosityNamespaceType.COMMODITY_GROUP} i18nKey={hit.commodityGroupId} />
                </Link>
              </Stack>
            ) : null}

            {hit.parent.status ? (
              <Stack direction="row" spacing={1}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {t('status')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.primary',
                  }}
                >
                  {tStatus(hit.parent.status)}
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        </Grid>
        <Grid size={3}>
          <Typography
            variant="subtitle1"
            sx={{
              textAlign: 'right',
            }}
          >
            {isNotNil(hit.valueFloat) ? format(hit.valueFloat) : t('emptyValue')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              textAlign: 'right',
            }}
          >
            <Trans
              t={t}
              i18nKey="amountUnitPrice"
              values={{
                amount: isNotNil(hit.quantityFloat)
                  ? formatNumber(NumberFormatPreset.DECIMAL, hit.quantityFloat)
                  : t('emptyValue'),
                price: hit.pricePerUnitFloat ? format(hit.pricePerUnitFloat) : t('emptyValue'),
              }}
              components={{
                CuriosityValue: <CuriosityValue ns={CuriosityNamespaceType.UNIT} i18nKey={hit.unitId} />,
              }}
            />
          </Typography>
        </Grid>
      </Grid>
    </ListItemButton>
  );
};
